import { motion } from 'framer-motion';
import { ServiceCard } from '@/components/Cards';
import { services } from '@/data/index';
import { IMAGES } from '@/assets/images';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';
import { Clock, Shield, Award, CheckCircle } from 'lucide-react';

export default function Services() {
  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.LARGE_BREEDS_3}
            alt="Professional grooming for large breeds"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight">
              Professional Grooming Services
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Specialized care for large and powerful breeds. Every service designed with strength, safety, and dignity in mind.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            <motion.div
              variants={staggerItem}
              className="text-center p-6 rounded-2xl bg-card border border-border"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Expert Handling</h3>
              <p className="text-muted-foreground text-sm">
                Certified professionals trained in large breed behavior and safety protocols
              </p>
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="text-center p-6 rounded-2xl bg-card border border-border"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <Award className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Premium Products</h3>
              <p className="text-muted-foreground text-sm">
                Professional-grade shampoos, conditioners, and tools designed for powerful coats
              </p>
            </motion.div>

            <motion.div
              variants={staggerItem}
              className="text-center p-6 rounded-2xl bg-card border border-border"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center">
                <Clock className="w-8 h-8 text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Rush Service</h3>
              <p className="text-muted-foreground text-sm">
                Adequate time allocated for each dog to ensure calm, stress-free grooming
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              Our Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive grooming packages tailored for large, powerful breeds
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {services.map((service) => (
              <motion.div key={service.id} variants={staggerItem}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springPresets.gentle}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-4 tracking-tight">
                What to Expect
              </h2>
              <p className="text-xl text-muted-foreground">
                Our grooming process is designed for safety, comfort, and exceptional results
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6"
            >
              <motion.div
                variants={staggerItem}
                className="flex gap-4 p-6 rounded-2xl bg-card border border-border"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Initial Assessment</h3>
                  <p className="text-muted-foreground">
                    We begin with a thorough evaluation of your dog's coat condition, temperament, and any special needs. This ensures we tailor our approach to your dog's specific requirements.
                  </p>
                </div>
              </motion.div>

              <motion.div
                variants={staggerItem}
                className="flex gap-4 p-6 rounded-2xl bg-card border border-border"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Professional Grooming</h3>
                  <p className="text-muted-foreground">
                    Our certified groomers use professional-grade equipment and techniques designed specifically for large breeds. We maintain calm, confident handling throughout the entire process.
                  </p>
                </div>
              </motion.div>

              <motion.div
                variants={staggerItem}
                className="flex gap-4 p-6 rounded-2xl bg-card border border-border"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Quality Check & Consultation</h3>
                  <p className="text-muted-foreground">
                    Before pickup, we conduct a final quality inspection and provide you with maintenance tips and recommendations for at-home care between grooming sessions.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.GROOMER_WORK_2}
            alt="Professional grooming facility"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
              Ready to Book?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Schedule your appointment today and experience professional grooming designed for powerful breeds.
            </p>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              transition={springPresets.snappy}
              className="inline-block px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 transition-colors"
            >
              Book Appointment
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
